import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { API_BASE, qk } from "@vorloop/api-client-react";

/**
 * Subscribes to the project's SSE event channel. Three events:
 *   - file_written / file_deleted → invalidate the file tree + the
 *     affected file's read cache.
 *   - preview_refresh             → ping the caller's `onPreviewRefresh`
 *     so the iframe bumps its src.
 *
 * Reconnects automatically — `EventSource` handles backoff for us.
 */
export interface ProjectEventsCallbacks {
  /** Fired when the server-side `run_preview` tool runs. */
  readonly onPreviewRefresh?: (note: string | null) => void;
}

export function useProjectEvents(
  projectId: string | null,
  callbacks?: ProjectEventsCallbacks,
) {
  const qc = useQueryClient();
  const previewCb = callbacks?.onPreviewRefresh;

  useEffect(() => {
    if (!projectId) return;
    // API_BASE is "" in dev (Vite proxy) and "https://api.vor.codes"
    // in prod. Without the prefix, the EventSource would hit
    // vor.codes/api/... → Cloudflare Pages SPA fallback returns
    // text/html and the browser aborts the SSE connection.
    const url = `${API_BASE}/api/projects/${projectId}/events`;
    const es = new EventSource(url, { withCredentials: true });

    function invalidate(path?: string) {
      qc.invalidateQueries({ queryKey: qk.projectFiles(projectId!) });
      if (path) {
        qc.invalidateQueries({ queryKey: ["projects", projectId!, "files", "read", path] });
      } else {
        qc.invalidateQueries({ queryKey: ["projects", projectId!, "files", "read"] });
      }
    }

    es.addEventListener("file_written", (e) => {
      try {
        const { path } = JSON.parse((e as MessageEvent).data) as { path?: string };
        invalidate(path);
      } catch {
        invalidate();
      }
    });
    es.addEventListener("file_deleted", (e) => {
      try {
        const { path } = JSON.parse((e as MessageEvent).data) as { path?: string };
        invalidate(path);
      } catch {
        invalidate();
      }
    });
    es.addEventListener("preview_refresh", (e) => {
      let note: string | null = null;
      try {
        const parsed = JSON.parse((e as MessageEvent).data) as { note?: string | null };
        note = parsed.note ?? null;
      } catch {
        // ignore
      }
      previewCb?.(note);
    });
    // hello / ping are no-ops; EventSource just keeps the connection alive.

    return () => {
      es.close();
    };
  }, [projectId, qc, previewCb]);
}
